import type React from "react";
import { Icon } from "../../ui/icons.js";

/**
 * The furniture of **Set up**: the numbered step, the card inside it, and the dial the loop's own section uses.
 *
 * Here rather than in each section because all five share it, and a checklist whose steps are drawn five slightly
 * different ways is a checklist somebody stops trusting.
 */

/**
 * One numbered section of the checklist, with a tick when it is answered.
 *
 * Set up used to be two screens and four cards in a grid, which is four things a person has to choose between
 * before doing the first one. Numbered and stacked, it reads as what it is: the order a new workspace needs.
 */
export function Step({ n, title, done, note, children }: {
  n: number; title: string; done: boolean; note?: string; children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-line bg-panel p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        {done
          ? <span className="w-[18px] h-[18px] rounded-full bg-good flex items-center justify-center shrink-0"><Icon name="check" size={11} className="text-white" /></span>
          : <span className="w-[18px] h-[18px] rounded-full border-[1.5px] border-line-2 text-faint text-[10px] font-bold flex items-center justify-center shrink-0">{n}</span>}
        <h2 className="text-[13.5px] font-semibold">{title}</h2>
        {note ? <span className="text-[11.5px] text-muted ml-auto">{note}</span> : null}
      </div>
      {children}
    </section>
  );
}

export function Card({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-line bg-panel p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Dial({ label, value, disabled, small, onChange }: {
  label: string; value: string; disabled: boolean; small?: boolean; onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{label}</span>
      <input
        defaultValue={value} disabled={disabled} onBlur={(e) => onChange(e.target.value)}
        className={`rounded-lg border border-line-2 bg-page px-2.5 py-1.5 text-[13px] tabular-nums disabled:opacity-50 ${small ? "w-16" : ""}`}
      />
    </label>
  );
}
